import DOMPurify from 'dompurify'

/**
 * Sanitiza URLs de imágenes usando DOMPurify para prevenir XSS
 * Permite blob URLs, data URLs y HTTP/HTTPS
 *
 * @param {string} url - URL a sanitizar
 * @param {string} fallback - URL de fallback si la validación falla (opcional)
 * @returns {string} - URL sanitizada o fallback
 */
export const sanitizeImageUrl = (url, fallback = '') => {
  if (!url) {
    return fallback
  }

  // Permitir blobs o data URLs internos
  if (url.startsWith('blob:') || url.startsWith('data:image/')) {
    return url
  }

  // Si es el fallback mismo, retornarlo directamente
  if (url === fallback) {
    return url
  }

  // Sanitizar con DOMPurify
  const sanitized = DOMPurify.sanitize(url, {
    ALLOWED_TAGS: [],
    ALLOWED_ATTR: []
  })

  // Bloquear javascript:
  if (sanitized.toLowerCase().startsWith('javascript:')) {
    return fallback
  }

  // Validar que sea una URL HTTP/HTTPS válida
  try {
    const urlObj = new URL(sanitized)
    if (urlObj.protocol !== 'http:' && urlObj.protocol !== 'https:') {
      return fallback
    }

    return sanitized
  } catch {
    return fallback
  }
}

/**
 * Sanitiza URLs de YouTube usando DOMPurify
 * Solo permite URLs de YouTube embed válidas con protocolo HTTPS
 *
 * @param {string} url - URL de YouTube a sanitizar
 * @returns {string|null} - URL sanitizada o null si no es válida
 */
export const sanitizeYoutubeUrl = (url) => {
  if (!url) {
    return null
  }

  // Sanitizar primero con DOMPurify
  const sanitized = DOMPurify.sanitize(url, {
    ALLOWED_TAGS: [],
    ALLOWED_ATTR: []
  })

  try {
    const urlObj = new URL(sanitized)
    // Solo permitir URLs de youtube.com o youtube-nocookie.com con https
    if (
      urlObj.protocol === 'https:' &&
      (urlObj.hostname === 'www.youtube.com' ||
        urlObj.hostname === 'youtube.com' ||
        urlObj.hostname === 'www.youtube-nocookie.com' ||
        urlObj.hostname === 'youtube-nocookie.com') &&
      urlObj.pathname.startsWith('/embed/')
    ) {
      return sanitized
    }
  } catch {
    return null
  }

  return null
}
